import { useState, useEffect, useCallback } from "react";
import fileDownload from "js-file-download";

import * as downloadsApi from "@/api/Downloads/downloads-api";

const MAX_RETRIES = 3;

export const useDownloads = (downloads = []) => {
	const [localDownloads, setLocalDownloads] = useState([]);

	useEffect(() => {
		setLocalDownloads(downloads ? [...downloads] : []);
	}, [downloads]);

	const downloadFile = useCallback(async (download) => {
		const retries = download.retries || 0;

		try {
			const blob = await downloadsApi.downloadFile({
				url: download.url,
				params: download.params,
				api: download.api || "lumen",
			});

			fileDownload(blob, download.name);
			return { success: true };
		} catch (error) {
			console.error("Error downloading file:", error);

			if (retries < MAX_RETRIES) {
				return { success: false, error, shouldRetry: true, retries: retries + 1 };
			}

			return { success: false, error, shouldRetry: false };
		}
	}, []);

	const executeExport = useCallback(async (download) => {
		const retries = download.retries || 0;

		try {
			await downloadsApi.executeExport({
				url: download.url,
				params: download.params,
				key: download.key,
				api: download.api || "lumen",
			});
			return { success: true };
		} catch (error) {
			console.error("Error executing export:", error);

			if (retries < MAX_RETRIES) {
				return { success: false, error, shouldRetry: true, retries: retries + 1 };
			}

			return { success: false, error, shouldRetry: false };
		}
	}, []);

	const checkExportStatus = useCallback(async (uuid, api = "lumen") => {
		try {
			const data = await downloadsApi.checkExportStatus({ uuid, api });

			const status = data?.status || "Task not found";

			if (status === "Task not found" || status.includes("failed")) {
				return { status: "error", message: status };
			}

			if (status === "completed") {
				const response = await downloadsApi.downloadExportedFile({ uuid, api });

				if (response?.url) {
					return { status: "completed", url: response.url };
				}
			}

			return { status };
		} catch (error) {
			console.error("Error checking export status:", error);
			return { status: "error", error };
		}
	}, []);

	const updateDownloadState = useCallback((key, updates) => {
		setLocalDownloads((prev) => prev.map((doc) => (doc.key === key ? { ...doc, ...updates } : doc)));
	}, []);

	const removeDownload = useCallback((key) => {
		setLocalDownloads((prev) => prev.filter((doc) => doc.key !== key));
	}, []);

	return {
		localDownloads,
		downloadFile,
		executeExport,
		checkExportStatus,
		updateDownloadState,
		removeDownload,
		setLocalDownloads,
	};
};
